import { cn } from "@/libs/cn";
import { Skeleton } from "@/modules/common/components";
import { AdminDashboardSummaryResponse } from "@/service/response/admin-dashboard";
import { AlertCircle, CheckCircle2, ShieldAlert, UserPlus, Zap } from "lucide-react";

interface DashboardHealthParams {
  summary?: AdminDashboardSummaryResponse;
  isLoading?: boolean;
  className?: string;
}

const HealthChart = ({ label, isLoading, value, icon }: { label: string; isLoading: boolean; value?: number; icon: React.ReactNode }) => {
  if (isLoading)
    return (
      <div className="flex w-full h-13.5 justify-between bg-white rounded-lg shadow gap-2 p-4 items-start">
        <Skeleton />
        <Skeleton />
      </div>
    );

  return (
    <div className="flex justify-between items-center gap-2 border border-ui-border bg-white rounded-xl p-2">
      <div className="flex min-w-0 items-center gap-4">
        <div className="shrink-0">{icon}</div>
        <p className="min-w-0 wrap-break-word text-[14px] text-ui-text/60 font-semibold leading-tight">{label}</p>
      </div>

      <p className="shrink-0 text-[14px] text-ui-text font-bold leading-tight">{value ?? "-"}</p>
    </div>
  );
};

const DashboardHealth = ({ summary, isLoading = false, className }: DashboardHealthParams) => {
  return (
    <div
      className={cn(
        "flex flex-col h-full w-full lg:w-95 shrink-0 rounded-xl border border-[#E2E8F0] bg-white text-center gap-4 p-4",
        className,
      )}
    >
      <div className="flex flex-col items-start">
        <p className="text-[16px] font-bold text-ui-primary">Saúde da plataforma</p>
        <p className="text-[14px] text-ui-text-muted">Status operacional consolidado</p>
      </div>

      <div className="flex flex-col gap-2">
        <HealthChart
          value={summary?.platformHealth?.activeManagementCompanies}
          isLoading={isLoading}
          label="Ativos"
          icon={
            <div className="flex items-center justify-center p-2 bg-emerald-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 font-bold" />
            </div>
          }
        />

        <HealthChart
          value={summary?.platformHealth?.inactiveManagementCompanies}
          isLoading={isLoading}
          label="Inativos"
          icon={
            <div className="flex items-center justify-center p-2 bg-amber-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 font-bold" />
            </div>
          }
        />

        <HealthChart
          value={summary?.platformHealth?.newRegistrationsLast30Days}
          isLoading={isLoading}
          label="Novos cadastros (30d)"
          icon={
            <div className="flex items-center justify-center p-2 bg-sky-100 rounded-lg">
              <UserPlus className="w-5 h-5 text-sky-600 font-bold" />
            </div>
          }
        />

        <HealthChart
          value={summary?.platformHealth?.managementCompaniesWithoutAdmin}
          isLoading={isLoading}
          label="Gestoras sem Representante"
          icon={
            <div className="flex items-center justify-center p-2 bg-rose-100 rounded-lg">
              <ShieldAlert className="w-5 h-5 text-rose-600 font-bold" />
            </div>
          }
        />
      </div>

      <div className="w-full max-w-md rounded-2xl bg-[#f0f6fc] border border-sky-100 p-6 flex flex-col gap-2 shadow-sm">
        <div className="text-start">
          <p className="text-xs items-start font-bold text-[#003e92] tracking-wider uppercase">Estrutura da Plataforma</p>
        </div>

        <div className="font-mono text-sm text-slate-700 leading-relaxed select-none">
          <div className="flex items-center gap-2 font-medium">
            <Zap className="w-4 h-4 text-lime-500 fill-lime-500/20 stroke-[2.5]" />
            <span>POUPenergia</span>
          </div>

          <div className="pl-1 pt-0.5 space-y-0.5 text-slate-600">
            <div className="flex items-baseline">
              <span className="text-slate-400 shrink-0">├── </span>
              <span>Gestoras ({summary?.overview.totalManagementCompanies.total ?? "-"})</span>
            </div>

            <div>
              <div className="flex items-baseline ml-4">
                <span className="text-slate-400 shrink-0">├── </span>
                <span>Gestão de Terceiros ({summary?.overview.thirdPartyManagementCompanies.total ?? "-"})</span>
              </div>

              <div className="flex items-baseline ml-4">
                <span className="text-slate-400 shrink-0">└── </span>
                <span>Gestão Própria ({summary?.overview.ownManagementCompanies.total ?? "-"})</span>
              </div>
            </div>

            <div className="flex items-baseline">
              <span className="text-slate-400 shrink-0">└── </span>
              <span>Admins Globais ({summary?.overview.totalUsers.total ?? "-"})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHealth;
